import { createContext, useState, useCallback, useEffect, useContext } from 'react';

// Initialize with an empty object so useContext never returns undefined
export const CommonContext = createContext({});

export const CommonProvider = ({ children }) => {
    const [fieldValues, setFieldValues] = useState({});

   const [ncduser, setNcdUser] = useState(null);

    useEffect(() => {
        const storedUser = localStorage.getItem("NCD_loggedinUserdetails");
        if (storedUser) {
            setNcdUser(JSON.parse(storedUser));
        }
    }, []);

    useEffect(() => {
        if (ncduser) {
            localStorage.setItem(
                "NCD_loggedinUserdetails",
                JSON.stringify(ncduser)
            );
        }
    }, [ncduser]);

    // Helper to format DD-MM-YYYY to YYYY-MM-DD
    const formatDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string' || !dateStr.includes('-')) return dateStr;
        const parts = dateStr.split('-');
        // If already YYYY-MM-DD, return as is
        if (parts[0].length === 4) return dateStr;
        // Convert DD-MM-YYYY to YYYY-MM-DD
        const [day, month, year] = parts;
        return `${year}-${month}-${day}`;
    };

    /**
     * Update field values from API data
     * @param {Object} apiData - The API response data
     * @param {Object} programsObj - The programs object to identify which program is active
     * @param {Object} fieldMapping - Optional mapping of API fields to context fields
     *                                 Example: { apiWeight: 'weight', apiHeight: 'height' }
     *                                 If not provided, uses direct field names
     * @param {string} dateField - Optional field name for the date in apiData (default: 'personalExamDate')
     */
    const updateFieldValues = useCallback((apiData, programsObj, fieldMapping = null, dateField = 'personalExamDate') => {
        if (!apiData || !programsObj) return;

        // Find key where value is 'programm'
        const programName = Object.keys(programsObj).find(key => programsObj[key] === 'programm') || "unknown";

        setFieldValues(prev => {
            const newState = { ...prev };
            const formattedDate = formatDate(apiData[dateField]);

            // If no field mapping provided, use default common fields
            const defaultFields = ['height', 'weight', 'bmi', 'bloodPressure', 'temperature', 'pulse', 'respiratoryRate', 'spo2'];

            // Create the mapping: { contextField: apiField }
            let fieldsToProcess = {};

            if (fieldMapping) {
                // Use provided mapping
                fieldsToProcess = fieldMapping;
            } else {
                // Use default 1:1 mapping for common fields
                defaultFields.forEach(field => {
                    fieldsToProcess[field] = field;
                });
            }

            // Process each field mapping
            Object.entries(fieldsToProcess).forEach(([contextField, apiField]) => {
                // Check if value exists in API response
                if (apiData[apiField] !== undefined && apiData[apiField] !== null && apiData[apiField] !== '') {
                    if (!newState[contextField]) newState[contextField] = [];

                    // Duplicate check: same program, same date
                    const exists = newState[contextField].some(r =>
                        r.date === formattedDate && r.program === programName
                    );

                    if (!exists) {
                        newState[contextField] = [
                            ...newState[contextField],
                            {
                                program: programName,
                                value: String(apiData[apiField]),
                                date: formattedDate
                            }
                        ];
                    }
                }
            });

            return newState;
        });
    }, []);

    return (
        <CommonContext.Provider value={{ fieldValues, setFieldValues, updateFieldValues, ncduser, setNcdUser }}>
            {children}
        </CommonContext.Provider>
    );
};

export const useCommon = () => {
    return useContext(CommonContext);
};